import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Checkbox, MenuItem, Select, Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  GridCellParams,
  GridFilterItem,
  GridValueFormatterParams,
  GridRowParams,
  GridFilterModel,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import {
  debounce,
  difference,
  find,
  intersection,
  isEqual,
  orderBy,
  uniqBy,
} from "lodash";
import moment from "moment";

import DataGrid from "components/shared/XDataGrid";
import XNoRowsOverlay from "components/shared/XNoRowsOverlay";
import Pagination from "components/shared/Pagination";
import { DESIGNS } from "helpers/apis";
import useAxios from "helpers/axios";
import useApp, { ProductLevel } from "hooks/useApp";
import useDataTable from "hooks/useXDataTable";
import { colors } from "theme";
import { DesignStatus, IDesign, IDesignColDef } from "types/design";

const useStyles = makeStyles((theme: Theme) => ({
  panelsTable: {
    width: "100%",
    backgroundColor: theme.palette.background.paper,
    border: "none",
    color: colors.nero[300],
    fontSize: 12,
    lineHeight: "17px",
    letterSpacing: 0.5,
    "& .MuiDataGrid-columnHeader": {
      [theme.breakpoints.down("lg")]: {
        padding: 0,
        "&.version .MuiDataGrid-columnHeaderTitleContainer": {
          padding: 0,
        },
      },
    },
    "& .MuiDataGrid-cell": {
      display: "flex",
      alignItems: "center",
      whiteSpace: "pre-wrap",
      lineHeight: "inherit !important",
      maxHeight: "unset !important",
      [theme.breakpoints.down("lg")]: {
        paddingLeft: 6,
      },
      "&.justify-content-end": {
        justifyContent: "flex-end",
      },
    },
    "& .MuiDataGrid-row": {
      maxHeight: "unset !important",
    },
    "& .gray-row": {
      background: colors.grey[700],
      "&:hover": {
        background: colors.grey[700],
      },
    },
    "& .MuiCheckbox-root": {
      padding: 0,
    },
  },
  panelStatus: {
    width: "100%",
    fontSize: 12,
    lineHeight: "17px",
    letterSpacing: 0.5,
    "&::before, &::after": {
      display: "none",
    },
    "& svg": {
      color: colors.grey[300],
    },
    "& > .MuiSelect-select:focus": {
      backgroundColor: "transparent",
    },
  },
}));

export const DataTable = ({
  currentPage,
  filteredRows,
  rowsData,
  selectedDesigns,
  setSelectedDesigns,
  setCurrentPage,
  setRowsData,
}: {
  currentPage: number;
  filteredRows: IDesign[];
  rowsData: IDesign[];
  selectedDesigns: number[];
  setSelectedDesigns: Function;
  setCurrentPage: Function;
  setRowsData: Function;
}) => {
  const [pageSize, setPageSize] = useState<number>(50);
  const [totalCount, setTotalCount] = useState<number>(0);
  const { productLevel } = useApp();
  const columns: IDesignColDef[] = [
    {
      field: "mpId",
      headerName: "ID",
      flex: 1,
      disableColumnMenu: true,
      hideSortIcons: true,
      sortable: false,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 4,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "version",
      headerName: "Version",
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      headerClassName: "version",
      cellClassName: "justify-content-end",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 2,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => {
        return productLevel !== ProductLevel.MVP ? (
          <Select
            displayEmpty
            value={params.value}
            onChange={(event) => {
              event.stopPropagation();
              (find(rowsData, { id: params.id }) as any).status =
                event.target.value;
              setRowsData([...rowsData]);
            }}
            MenuProps={{}}
            inputProps={{}}
            className={classes.panelStatus}
          >
            <MenuItem value={DesignStatus.PUBLISHED}>Published</MenuItem>
            <MenuItem value={DesignStatus.UNPUBLISHED}>Unpublished</MenuItem>
          </Select>
        ) : (
          <span>{(params?.value as any) ?? ""}</span>
        );
      },
    },
    {
      field: "uploadedAt",
      headerName: "Upload Date",
      flex: 2,
      sortable: false,
      disableColumnMenu: true,
      valueGetter: (params: GridValueFormatterParams) => {
        return moment(params.value as string).format("YYYY-MM-DD");
      },
    },
  ];
  const dataGridRef = useRef<any>(null);

  const { CustomToolbar, filter, filterButtonEl, isFiltered, setFilter } =
    useDataTable({ columns });

  const selectedMpIds = useMemo(() => {
    return rowsData
      .filter((design) => selectedDesigns.includes(design.id))
      .map((design) => design.mpId);
  }, [selectedDesigns, rowsData]);
  const classes = useStyles();

  const filterModelChange = useCallback(
    (filterModel: GridFilterModel) => {
      if (!isEqual(filter, filterModel.items)) {
        const newRows = dataGridRef.current.getSelectedRows();
        setFilter({ ...filterModel });
        // Deselect designs out of the filter
        setSelectedDesigns((oldDesigns: any) =>
          intersection(Array.from(newRows.keys()), oldDesigns)
        );
      }
    },
    [filter, setFilter, setSelectedDesigns]
  );

  const { fetchData, loading } = useAxios({
    config: {
      url: DESIGNS,
    },
    callOnInit: false,
    successCallback: ({ response }: any) => {
      if (Array.isArray(response.data)) {
        setRowsData(response.data);
        setTotalCount(response.totalCount ?? 0);
      } else {
        setRowsData([]);
        setTotalCount(0);
        setCurrentPage(1);
      }
    },
  });
  const { current: debouncedFetch } = useRef(
    debounce((currentPage, filter?: GridFilterItem) => {
      fetchData({
        params: {
          includeUnpublished: true,
          keepDuplicates: true,
          pageNumber: currentPage,
          pageSize: pageSize,
          ...(filter
            ? {
                filter: JSON.stringify({
                  id: filter.id,
                  column_field: filter.field,
                  operator_value: filter.operator,
                  value: filter.value,
                }),
              }
            : {}),
        },
      });
    }, 500)
  );

  const getRowClassName = useCallback(
    (params: GridRowParams) => {
      const design = params.row;

      if (
        !selectedDesigns.includes(design.id) &&
        selectedMpIds.includes(design.mpId)
      ) {
        return "gray-row";
      }
      return "";
    },
    [selectedDesigns, selectedMpIds]
  );

  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    if (debouncedFetch) {
      debouncedFetch(
        currentPage,
        filter.items?.[0]?.value ? filter.items[0] : undefined
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFetch, filter.items?.[0], currentPage]);

  return (
    <DataGrid
      autoHeight
      apiRef={dataGridRef}
      checkboxSelection
      components={{
        NoRowsOverlay: () => <XNoRowsOverlay text="No Designs" />,
        Pagination: () => (
          <Pagination
            totalCount={totalCount}
            pageSize={pageSize}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        ),
        Toolbar: CustomToolbar,
        Checkbox: forwardRef(
          (props, ref: ForwardedRef<HTMLButtonElement | null>) => (
            <Checkbox
              disabled={!isFiltered}
              ref={ref}
              {...props}
              color="secondary"
            />
          )
        ),
      }}
      componentsProps={{
        panel: {
          anchorEl: filterButtonEl,
        },
      }}
      filterModel={filter}
      getRowClassName={getRowClassName}
      onFilterModelChange={filterModelChange}
      rowHeight={49}
      hideFooterSelectedRowCount
      hideFooterRowCount
      loading={loading}
      onPageSizeChange={(pageSize: number) => setPageSize(pageSize)}
      onPageChange={(page: number) => setCurrentPage(page)}
      page={currentPage - 1}
      pageSize={pageSize}
      rows={filteredRows}
      columns={columns}
      className={classes.panelsTable}
      rowSelectionModel={selectedDesigns}
      onRowSelectionModelChange={(selectionModel: GridRowSelectionModel) => {
        if (selectedDesigns.length < selectionModel.length) {
          // Deselect any designs with duplicated mp_id on another selection
          let changedDesignIds = difference(selectionModel, selectedDesigns);
          const changedDesigns = uniqBy(
            orderBy(
              rowsData.filter((row) => changedDesignIds.includes(row.id)),
              ["version"],
              ["desc"]
            ),
            (row) => row.mpId
          );
          changedDesignIds = changedDesigns.map((row) => row.id);
          const mpIds = changedDesigns.map((row) => row.mpId);
          const designIds = rowsData
            .filter(
              (row) =>
                selectionModel.includes(row.id) &&
                (changedDesignIds.includes(row.id) || !mpIds.includes(row.mpId))
            )
            .map((row) => row.id);
          setSelectedDesigns(designIds);
        } else {
          setSelectedDesigns(selectionModel);
        }
      }}
    />
  );
};
